// Light, dark, or the device's (BL-135): the person's pick in their own menu (Frame.tsx). The tokens
// already draw dark under prefers-color-scheme unless <html data-theme="light">, and dark whenever
// data-theme="dark" (packages/tokens css.ts), so a pick is that attribute, or none for the device's.
// There is no per-person preference on the server, so it is remembered on this device, in
// localStorage; public/theme-boot.js sets it before the page draws, so it never flashes the other way.
// Storage can be refused (a private window, blocked site data): every read and write is guarded, and
// the device's choice is the answer then.
// useShownTheme follows it live (BL-109's light and dark pictures): a pick in the menu changes the
// attribute, and on "Match my device" the device's own change is the media query's.

import { useSyncExternalStore } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'device';

/** The localStorage key, read by public/theme-boot.js too. */
export const THEME_KEY = 'flow.theme';

export const THEME_CHOICES: readonly [ThemeChoice, string][] = [
  ['light', 'Light'],
  ['dark', 'Dark'],
  ['device', 'Match my device'],
];

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const store = (): Store | null => {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

/** The pick remembered on this device; the device's when none is, or storage can't be read. */
export function readTheme(s: Store | null = store()): ThemeChoice {
  try {
    const v = s?.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'device';
  } catch {
    return 'device';
  }
}

/** Remembers a pick (the device's is no entry); false when storage refused it. */
export function saveTheme(choice: ThemeChoice, s: Store | null = store()): boolean {
  try {
    if (!s) return false;
    if (choice === 'device') s.removeItem(THEME_KEY);
    else s.setItem(THEME_KEY, choice);
    return true;
  } catch {
    return false;
  }
}

/** Draws a pick: <html data-theme> for light or dark, none to follow the device. */
export function applyTheme(choice: ThemeChoice, root: HTMLElement = document.documentElement): void {
  if (choice === 'device') delete root.dataset.theme;
  else root.dataset.theme = choice;
}

/** Whether the page draws light or dark now: the pick, or the device's. */
export function shownTheme(
  choice: ThemeChoice = readTheme(),
  deviceDark: boolean = typeof matchMedia === 'function' &&
    matchMedia('(prefers-color-scheme: dark)').matches,
): 'light' | 'dark' {
  return choice === 'device' ? (deviceDark ? 'dark' : 'light') : choice;
}

const DARK = '(prefers-color-scheme: dark)';

/** Where the page's theme is drawn: <html>, the device's dark query, and the browser's observer. */
export interface ThemeWorld {
  root: HTMLElement | null;
  media: Pick<MediaQueryList, 'matches' | 'addEventListener' | 'removeEventListener'> | null;
  Observer: typeof MutationObserver | null;
}

/** The page's own; each is null where there is none (a server render, a test without a DOM). */
export function themeWorld(): ThemeWorld {
  return {
    root: typeof document === 'undefined' ? null : document.documentElement,
    media: deviceQuery(),
    Observer: typeof MutationObserver === 'function' ? MutationObserver : null,
  };
}

function deviceQuery(): ThemeWorld['media'] {
  try {
    return typeof matchMedia === 'function' ? matchMedia(DARK) : null;
  } catch {
    return null;
  }
}

/** What the page draws now: <html data-theme>'s pick, or the device's when it has none. */
export function drawnTheme(w: ThemeWorld = themeWorld()): 'light' | 'dark' {
  const t = w.root?.dataset.theme;
  return shownTheme(t === 'light' || t === 'dark' ? t : 'device', w.media?.matches ?? false);
}

/** Calls `changed` when either can change what's drawn; the answer stops watching. */
export function watchTheme(changed: () => void, w: ThemeWorld = themeWorld()): () => void {
  const seen = w.root && w.Observer ? new w.Observer(() => changed()) : null;
  if (seen && w.root) seen.observe(w.root, { attributes: true, attributeFilter: ['data-theme'] });
  w.media?.addEventListener('change', changed);
  return () => {
    seen?.disconnect();
    w.media?.removeEventListener('change', changed);
  };
}

/** Light or dark as the page draws it, and again whenever that changes. */
export function useShownTheme(): 'light' | 'dark' {
  return useSyncExternalStore(
    (changed) => watchTheme(changed),
    () => drawnTheme(),
    () => 'light',
  );
}
